import React, { Component } from 'react';
import {
  inject,
  observer,
} from 'mobx-react';
import {
  ErrorMessage,
  Field,
  Form,
  Formik,
} from 'formik';
import * as yup from 'yup';
import {
  Row,
  Col,
  ControlLabel,
  FormGroup,
  FormControl,
  Checkbox,
  Alert,
  Button,
} from 'react-bootstrap';
import LoadingButton from '../../components/LoadingButton/LoadingButton';
import PageLoading from '../../components/PageLoading/PageLoading';
import {
  Portlet,
  PortletBody,
  PortletCaption,
  PortletTitle,
} from '../../components/Bootstrap/Portlet';
import { withTranslation } from 'react-i18next';

class MixForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      error: null,
    };
    this.anchor = React.createRef();
  }

  componentDidMount () {
    this.anchor.current.scrollIntoView();
  }

  isOnlineMix = () => {
    const { mixStore } = this.props;
    const { selectedId } = mixStore;
    return !!mixStore.getOnlineMixById(selectedId);
  };

  getInitialValues() {
    return {
      itemCode: '',
      itemDescription: '',
      unitCode: '',
      slump: 0,
      airPercent: 0,
      waterCementRatio: 0,
      maxDropSize: 0,
      mixingTime: 0,
      maxWater: 0,
      taxable: false,
      slumpTableId: '',
      offline: true,
    };
  }

  render() {
    const { mixStore, materialStore, slumpTableStore, notifierStore, onCancel, t } = this.props;
    const { selectedId } = mixStore;
    const { slumpTables } = slumpTableStore;
    const units = materialStore.unit || [];
    const mix = !selectedId
      ? this.getInitialValues()
      : mixStore.getOnlineMixById(selectedId) || mixStore.getMixById(selectedId) || this.getInitialValues();

    return !units.length ? '' : (
      <Portlet>
        <PortletTitle>
          <PortletCaption>{t('Mix')} {selectedId ? mix.itemCode : ''}</PortletCaption>
        </PortletTitle>
        <PortletBody>
          <div ref={this.anchor}>
            <Formik
              enableReinitialize
              initialValues={mix}
              validationSchema={yup.object().shape({
                itemCode: yup.string().required('Required!'),
                unitCode: yup.string().required('Required!'),
              })}
              onSubmit={(values, actions) => {
                this.setState({
                  error: null,
                });
                let promise;
                if (this.isOnlineMix()) {
                  promise = mixStore.updateMix(selectedId, Number(values.slumpTableId) || '');
                } else {
                  promise = mixStore.addMix(values);
                }

                promise
                  .then(() => {
                    notifierStore.showSuccess('Saved');
                    actions.setSubmitting(false);
                    onCancel();
                  })
                  .catch((e) => {
                    this.setState({
                      error: `Couldn't save: ${e.message}`,
                    });
                    actions.setValues(values);
                    actions.setSubmitting(false);
                  });

              }
              }
            >{({ handleChange,
                setValues,
                values,
                errors,
                touched,
                isSubmitting,
              }) => (
                <Form className={'mix-form'}>
                  {isSubmitting &&
                  <PageLoading/>
                  }
                  <Row>
                    <Col sm={3} xs={6}>
                      <FormGroup className={errors.itemCode && touched.itemCode? 'has-error' : ''}>
                        <ControlLabel>{t('Code')}</ControlLabel>
                        <Field name="itemCode"
                          placeholder={t('Code')}
                          disabled={this.isOnlineMix()}
                          value={values.itemCode || ''}
                          className="form-control input-sm" />
                        <ErrorMessage component="span" name="itemCode" className="help-block" />
                      </FormGroup>
                    </Col>
                    <Col sm={3} xs={6}>
                      <FormGroup>
                        <ControlLabel>{t('Description')}</ControlLabel>
                        <Field name="itemDescription"
                          placeholder={t('Description')}
                          disabled={this.isOnlineMix()}
                          value={values.itemDescription || ''}
                          className="form-control input-sm" />
                      </FormGroup>
                    </Col>
                    <Col sm={3} xs={6}>
                      <FormGroup className={errors.unitCode && touched.unitCode? 'has-error' : ''}>
                        <ControlLabel>{t('Unit')}</ControlLabel>
                        <FormControl
                          name="unitCode"
                          componentClass="select"
                          disabled={this.isOnlineMix()}
                          value={values.unitCode || ''}
                          onChange={e => {
                            const unitCode = e.target.value;
                            const unit = units.find(v => v.code === unitCode);
                            setValues({
                              ...values,
                              unitCode,
                              unitId: unit.id,
                              unitSymbol: unit.symbol || unit.name,
                            });
                          }}
                          bsSize="sm"
                        >
                          <option value=""/>
                          {units.map(option => (
                            <option key={option.id} value={option.code}>
                              {option.symbol || option.name}
                            </option>
                          ))}
                        </FormControl>
                        <ErrorMessage component="span" name="unitCode" className="help-block" />
                      </FormGroup>
                    </Col>
                    <Col sm={3} xs={6}>
                      <FormGroup>
                        <ControlLabel>{t('Slump')}</ControlLabel>
                        <FormControl
                          name="slump"
                          type="text"
                          value={values.slump || ''}
                          disabled={this.isOnlineMix()}
                          onChange={handleChange}
                          bsSize="sm"
                        />
                      </FormGroup>
                    </Col>
                    <Col sm={3} xs={6}>
                      <FormGroup>
                        <ControlLabel>{t('Air Volume')} %</ControlLabel>
                        <FormControl
                          name="airPercent"
                          type="number"
                          value={values.airPercent}
                          disabled={this.isOnlineMix()}
                          onChange={handleChange}
                          bsSize="sm"
                        />
                      </FormGroup>
                    </Col>
                    <Col sm={3} xs={6}>
                      <FormGroup>
                        <ControlLabel>{t('Ctr W/C Ratio')}</ControlLabel>
                        <FormControl
                          name="waterCementRatio"
                          type="number"
                          value={values.waterCementRatio}
                          disabled={this.isOnlineMix()}
                          onChange={handleChange}
                          bsSize="sm"
                        />
                      </FormGroup>
                    </Col>
                    <Col sm={3} xs={6}>
                      <FormGroup>
                        <ControlLabel>{t('Maximum Drop Size')}</ControlLabel>
                        <FormControl
                          name="maxDropSize"
                          type="number"
                          value={values.maxDropSize}
                          disabled={this.isOnlineMix()}
                          onChange={handleChange}
                          bsSize="sm"
                        />
                      </FormGroup>
                    </Col>
                    <Col sm={3} xs={6}>
                      <FormGroup>
                        <ControlLabel>{t('Mixing Time')}</ControlLabel>
                        <FormControl
                          name="mixingTime"
                          type="number"
                          value={values.mixingTime}
                          disabled={this.isOnlineMix()}
                          onChange={handleChange}
                          bsSize="sm"
                        />
                      </FormGroup>
                    </Col>
                    <Col sm={3} xs={6}>
                      <FormGroup>
                        <ControlLabel>{t('Maximum Water')}</ControlLabel>
                        <FormControl
                          name="maxWater"
                          type="number"
                          value={values.maxWater}
                          disabled={this.isOnlineMix()}
                          onChange={handleChange}
                          bsSize="sm"
                        />
                      </FormGroup>
                    </Col>
                    <Col sm={3} xs={6}>
                      <FormGroup className={errors.slumpTableId && touched.slumpTableId? 'has-error' : ''}>
                        <ControlLabel>{t('Slump Table')}</ControlLabel>
                        <FormControl
                          name="slumpTableId"
                          componentClass="select"
                          value={values.slumpTableId || ''}
                          onChange={handleChange}
                          bsSize="sm"
                        >
                          <option value=""/>
                          {slumpTables.map(option => (
                            <option key={option.id} value={option.id}>
                              {option.code} : {option.description}
                            </option>
                          ))}
                        </FormControl>
                        <ErrorMessage component="span" name="unitCode" className="help-block" />
                      </FormGroup>
                    </Col>
                    <Col sm={3} xs={6}>
                      <FormGroup>
                        <Checkbox
                          inline
                          className="mt-checkbox mt-checkbox-outline"
                          name="taxable"
                          disabled={this.isOnlineMix()}
                          value={values.taxable}
                          checked={!!values.taxable}
                          onChange={handleChange}
                        >
                          <span />
                        </Checkbox>
                        <ControlLabel>{t('Taxable')}</ControlLabel>
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col sm={12} className="text-right">
                      {!!this.state.error && <Alert bsStyle="danger">{this.state.error}</Alert>}
                      <Button onClick={onCancel}>{t('Close')}</Button>
                      {' '}
                      <LoadingButton
                        bsStyle="primary"
                        type="submit"
                        label={t('Save')}
                        loadingLabel={t('Save')}
                        isLoading={isSubmitting}
                      />
                    </Col>
                  </Row>
                </Form>
              )}
            </Formik>
          </div>
        </PortletBody>
      </Portlet>
    );
  }
}

export default withTranslation()(inject('mixStore', 'materialStore', 'slumpTableStore', 'notifierStore')(observer(MixForm)));
